import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
} from '@mui/material';
import CachedIcon from '@mui/icons-material/Cached';
import AddIcon from '@mui/icons-material/Add';
import CircleIcon from '@mui/icons-material/Circle';
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { ROUTE_LOGIN, ROUTE_THING_CREATEEDIT, ROUTE_THING_INFOMAIN, THING_ID } from '../../constants';
import ButtonBox from '../../components/ButtonBox';
import IconButtonBox from '../../components/IconButtonBox';
import NewTableBox, { RowData } from '../../components/NewTableBox';
import DialogBox from '../../components/DialogBox';
import '../../style/Thing.css';
import ThingApi from '../../api/ThingApi';
import useTableStore from '../../stores/TableStore';
import useThingStore from '../../stores/ThingStore';
import { addLocalStorage, useError } from '../../utils/functions';
import usePaginationStore from '../../stores/PaginationStore';

const Thing: React.FC = () => {
  const { setError } = useError();
  const navigate = useNavigate();
  const tableRef = useRef<any>(null);
  const { removeIdArray, setRemoveIdArray, setContinuousMenu } = useTableStore();
  const { reset, setIsEdit } = useThingStore();
  const [data, setData] = useState([]);
  const [deleteName, setDeleteName] = useState('');
  const [deleteId, setDeleteId] = useState('');
  const [openType, setOpenType] = useState('');
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [totalCount, setTotalCount] = useState(0);
  const { setBeforeCursor, setAfterCursor, setReQuery, pageAction, closeAction, resetStore } = usePaginationStore();

  const columns = [
    {
      field: "statusLight",
      title: "",
      width: "30px",
      render: (rowData: any) => {
        const statusClass = rowData.isOnline ? 'thing-color-active': 'thing-color-default';
        return (
          <CircleIcon
            fontSize="small"
            className={`${statusClass}`}
          />
        )

      }
    },
    { field: "name", title: "Name", isSearch: true, isLink: true, linkHandler: (val: any, row: RowData) => { setThingInfoData(row) } },
    { field: "lastCmdTime", title: "Last cmd time" },
    { field: "lastDataTime", title: "Last data time" },
    { field: "serialNumber", title: "Serial Number", isSearch: true },
    { field: "modelName", title: "Model Name", isSearch: true },
    {
      field: "isEon", title: "Thing Type", isFilter: true,
      filterOptions: [{ value: "true", label: "EoN" }, { value: "false", label: "Device" }],
      render: (rowData: any) => {
        const thingType = rowData.isEon ? "EoN" : "Device";
        return (
          <span>{thingType}</span>
        )
      }
    },
    {
      field: "status",
      title: "Status",
      isFilter: true,
      filterOptions: [{ value: "active", label: "active" }, { value: "transferring", label: "transferring" }, { value: "default", label: "default" }],
      render: (rowData: any) => {
        const statusClass = rowData.status === 'active'
          ? 'thing-status-active'
          : rowData.status === 'transferring'
            ? 'thing-status-transferring'
            : 'thing-status-default';

        return (
          <Box
            component="span"
            className={`thing-status-badge ${statusClass}`}
          >
            {rowData.status}
          </Box>
        )
      }
    },
    { field: "createdAt", title: "Created at", isSort: true },
  ];

  const setThingInfoData = (row: RowData) => {
    addLocalStorage(THING_ID, row.id);
    setIsEdit(true);
    navigate(ROUTE_THING_INFOMAIN);
  }

  const handleDelete = async () => {
    if (deleteId) {
      const res = await ThingApi.deleteThing(deleteId);

      if (res.code) {
        setError(res.message);
      } else {
        setDeleteId('');
        setReQuery(true);
        queryThingList();
        handleClose();
      }
    }
  };

  const queryThingList = async () => {
    setData([]);
    try {
      const { limit, beforeCursor, afterCursor } = usePaginationStore.getState();
      const paginationParams = {
        limit: limit,
        beforeCursor: beforeCursor,
        afterCursor: afterCursor,
      };

      const thingListResponse = await ThingApi.getThingList(paginationParams);
      console.log(thingListResponse);
      if (thingListResponse.code) {
        setError(thingListResponse.message);
      } else {
        setData(thingListResponse.data);

        if (thingListResponse.metadata && thingListResponse.metadata.total) {
          setTotalCount(thingListResponse.metadata.total);
        }

        if (thingListResponse.paging) {
          setAfterCursor(thingListResponse.paging.afterCursor);
          setBeforeCursor(thingListResponse.paging.beforeCursor);
        }
      }
    } catch (error) {
      console.error("Error fetching queryThingList data:", error);
    } finally {
      closeAction();
    }
  }

  const handleCancelSelect = () => {
    setRemoveIdArray([]);
    setTimeout(() => {
      if (tableRef.current?.onAllSelected) {
        tableRef.current.onAllSelected(false, []);
      }
    }, 100);
  }

  const handleDeleteSelect = async () => {
    let hasError = false;
    for (let id of removeIdArray) {
      const res = await ThingApi.deleteThing(id);
      if (res.code) {
        setError(res.message);
        hasError = true;
        break;
      }
    }

    if (!hasError) {
      setReQuery(true);
      queryThingList();
      handleCancelSelect();
    }
  }

  useEffect(() => {
    localStorage.removeItem(THING_ID);
    setContinuousMenu(true);
    resetStore();
    queryThingList();
    reset();
  }, []);


  useEffect(() => {
    if (pageAction) {
      queryThingList();
    }
  }, [pageAction]);

  return (
    <Box>
      <Typography className="layout-title">
        Thing
      </Typography>
      <Box className="thing-box1">
        <Box />
        {removeIdArray.length > 0 ? (
          <Box>
            <IconButtonBox
              icon={<DeleteOutlineOutlinedIcon />}
              text='Delete'
              onClick={handleDeleteSelect} />
            <IconButtonBox
              icon={<ClearIcon />}
              text='Cancel'
              onClick={handleCancelSelect} />
          </Box>
        ) : (
          <Box>
            <IconButtonBox
              icon={<CachedIcon />}
              text='Refresh'
              onClick={queryThingList} />
            <ButtonBox
              text='Create new'
              onClick={() => navigate(ROUTE_THING_CREATEEDIT)}
              icon={<AddIcon />} />
          </Box>
        )}
      </Box>
      <NewTableBox
        columns={columns}
        data={data}
        showDelete={true}
        showDisplayHeader={true}
        showSelection={true}
        tableRef={tableRef}
        deleteFunction={(rowData) => { setDeleteName(rowData.name); setOpenType('confirmDelete'); setDeleteId(rowData.id); handleOpen(); }}
        totalCount={totalCount}
      />

      <DialogBox
        open={open}
        title={
          (openType === 'confirmDelete' || openType === 'doDelete') ?
            'Delete' : 'Token expired'
        }
        dialogContent={
          openType === 'confirmDelete' ?
            <>
              {deleteName} will be permanently deleted.
            </>
            : openType === 'doDelete' ?
              <>
                The thing does not exist.
              </>
              : openType === 'tokenMsg' ?
                <>
                  Your token expired. Please log in again
                </>
                : ""
        }
        showCancel={openType === 'confirmDelete'}
        onCancel={handleClose}
        onConfirm={() => {
          if (openType === 'confirmDelete') {
            handleDelete();
          } else if (openType === 'doDelete') {
            setOpenType('tokenMsg');
          } else if (openType === 'tokenMsg') {
            navigate(ROUTE_LOGIN);
          }
        }}
        isError={openType === 'confirmDelete'}
        confirmLabel={
          openType === "confirmDelete" ? "Delete"
            : openType === "doDelete" ? "OK"
              : openType === 'tokenMsg' ? "Login"
                : ""
        }
      />

    </Box >
  );
};

export default Thing;
